import React, { useEffect, useState, useCallback, useRef } from 'react';
import { lookupItemsGetId } from '../../../../Services';
import useFetchCampaignPerformance from './hooks/useFetchCampaignPerformance';
import {
  AutocompleteComponent,
  Inputs,
  PaginationComponent,
  SelectComponet,
} from '../../../../Components';
import {
  bottomBoxComponentUpdate,
  returnPropsByPermissions,
} from '../../../../Helper';
import { useDebouncedAction } from '../../../../Hooks/DebouncedAction';
import { useTranslation } from 'react-i18next';
import { PerformanceInsightsTranslationPath } from '../i18n/PerformanceInsightsLocale';
import CollapsibleTable from './components/CollapsibleTable';
import KPICard from './components/KPICard';
import ExportExcelButton from '../components/ExportExcelButton';
import { ButtonBase } from '@material-ui/core';
import PerformanceDateRangePicker from '../components/PerformanceDateRangePicker';
import { useKeyGenerator } from '../../../../Hooks/keyGenerator';
import { ExportCampaignPerformanceExcel } from '../../../../Services/PerformanceInsights/CampaignInsightsService';
import { PropertyComponent } from '../agent-performance/components/PropertyComponent';
import { GetAllCampaigns } from '../../../../Services';
import { GetAllBranches } from '../../../../Services/BranchesServices';
import { ViewBranchCampaignsPermissions } from '../ViewBranchCampaignsPermissions';

const CampaignPerformance = () => {
  const { t } = useTranslation(PerformanceInsightsTranslationPath);
  const [params, setParams] = useState(() => {
    const hasPermission = returnPropsByPermissions(
      ViewBranchCampaignsPermissions.ViewBranchCampaigns.permissionsId
    );
    const session = localStorage.getItem('session');
    const branchId = session ? JSON.parse(session).branchId : [0];
    return {
      pageIndex: 1,
      pageSize: 10,
      isActive: null,
      campaignIds: [],
      branchIds: hasPermission && branchId ? [branchId] :
        !hasPermission ? [] :
          [0],
      mediaDetailIds: [],
    };
  });
  const { data, loading } = useFetchCampaignPerformance(params);
  const [key, refresh] = useKeyGenerator();
  const [allCampaigns, setAllCampaigns] = useState({
    result: [],
    totalCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [selectedMediaTypes, setSelectedMediaTypes] = useState([]);
  const mediaSearchTimer = useRef(null);
  const [mediaLoading, setMediaLoading] = useState(false);
  const searchTimer = useRef(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);

  useEffect(() => {
    setBranchesLoading(true);
    GetAllBranches({ pageIndex: 1, pageSize: 100 })
      .then((res) => {
        setBranches(res?.result || []);
        setBranchesLoading(false);
      })
      .catch(() => {
        setBranches([]);
        setBranchesLoading(false);
      });
  }, []);

  useEffect(() => {
    bottomBoxComponentUpdate(
      <PaginationComponent
        pageIndex={params.pageIndex - 1}
        pageSize={params.pageSize}
        totalCount={data.totalCount}
        onPageIndexChanged={(pageIndex) => {
          setParams((prev) => ({ ...prev, pageIndex: pageIndex + 1 }));
        }}
        onPageSizeChanged={(pageSize) => {
          setParams((prev) => ({ ...prev, pageSize }));
        }}
      />
    );
  }, [params.pageIndex, params.pageSize, data.totalCount]);

  const debounceSetParams = useDebouncedAction(setParams, 300);

  const inputSearchHandler = (e, field) => {
    const { value } = e.target;
    debounceSetParams((prevParams) => {
      const newParams = { ...prevParams, pageIndex: 1 };
      newParams[field] = value;
      return newParams;
    });
  };

  const getAllCampaigns = useCallback(async (searchValue = '') => {
    setIsLoading(true);
    try {
      const res = await GetAllCampaigns({
        pageIndex: 1,
        pageSize: 50,
        propertyCampaignId: +searchValue,
      });
      if (res && res.result) {
        setAllCampaigns({
          result: res.result,
          totalCount: res.totalCount,
        });
      } else {
        setAllCampaigns({
          result: [],
          totalCount: 0,
        });
        console.error('Failed to fetch campaigns:', res);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setAllCampaigns({
        result: [],
        totalCount: 0,
      });
    }
    setIsLoading(false);
  }, []);

  const getAllMediaTypes = useCallback(async (searchValue = '') => {
    setMediaLoading(true);
    try {
      const res = await lookupItemsGetId({
        lookupTypeId: 1240,
        lookupParentId: null,
      });
      let mediaList = Array.isArray(res) ? res : res?.result || [];
      if (searchValue) {
        mediaList = mediaList.filter((m) =>
          (m.name || m.lookupItemName || '')
            .toLowerCase()
            .includes(searchValue.toLowerCase())
        );
      }
      setMediaTypes(
        mediaList.map((item) => ({
          id: item.lookupItemId || item.id,
          name: item.lookupItemName || item.name,
        }))
      );
    } catch (e) {
      setMediaTypes([]);
    }
    setMediaLoading(false);
  }, []);

  React.useEffect(() => {
    getAllMediaTypes();
  }, [getAllMediaTypes]);

  React.useEffect(() => {
    const session = localStorage.getItem('session');
    if (!session || !branches || branches.length === 0) return;

    const hasPermission = returnPropsByPermissions(
      ViewBranchCampaignsPermissions.ViewBranchCampaigns.permissionsId
    );
    const branchId = JSON.parse(session).branchId;
    const matchedBranch = branches.find((b) => b.branchId === branchId);

    if (hasPermission && branchId && matchedBranch) {
      setSelectedBranches([
        {
          branchId: branchId,
          branchName: matchedBranch.branchName || '',
        },
      ]);
      setParams((prev) => ({ ...prev, branchIds: [branchId] }));
    } else {
      setSelectedBranches([]);
        !hasPermission ?
          setParams((prev) => ({ ...prev, branchIds: [] }))
         :setParams((prev) => ({ ...prev, branchIds: [0] }))

    }
  }, [branches]);

  return (
    <div>
      <KPICard params={params} setParams={setParams} />
      <ExportExcelButton
        params={params}
        handler={ExportCampaignPerformanceExcel}
      />
      <div className='d-flex-column gap-1 py-3 px-2' key={key}>
        <div className='d-flex gap-4'>
          <Inputs
            inputPlaceholder={'campaign_name'}
            onInputChanged={(e) => inputSearchHandler(e, 'campaignName')}
            parentTranslationPath={PerformanceInsightsTranslationPath}
          />
          <Inputs
            inputPlaceholder={'platform'}
            onInputChanged={(e) => inputSearchHandler(e, 'platform')}
            parentTranslationPath={PerformanceInsightsTranslationPath}
          />
          <div className='w-33'>
            <PerformanceDateRangePicker
              onDateRangeChange={({ startDate, endDate }) =>
                setParams((prev) => ({
                  ...prev,
                  pageIndex: 1,
                  startDate,
                  endDate,
                }))
              }
            />
          </div>
        </div>
        <div className='d-flex gap-4'>
          <div className='w-33'>
            <SelectComponet
              data={[
                { label: 'Active', value: 'true' },
                { label: 'Inactive', value: 'false' },
              ]}
              emptyItem={{ value: null, text: t('campaign_status') }}
              value={
                params.isActive !== null
                  ? params.isActive
                    ? 'true'
                    : 'false'
                  : null
              }
              onSelectChanged={(e) => {
                const isActive = e ? (e === 'true' ? true : false) : null;
                setParams((prev) => ({ ...prev, isActive }));
              }}
              textInput={'label'}
              valueInput={'value'}
            />
          </div>
          <div className='w-33'>
            <PropertyComponent
              parentTranslationPath={PerformanceInsightsTranslationPath}
              translationPath=''
              IDView={true}
              onInputChanged={(e) =>
                setParams((prev) => ({ ...prev, propertyIds: e.propertyIds }))
              }
            />
          </div>
          <div className='w-33'>
            <div className='dialog-content-item'>
              <AutocompleteComponent
                idRef='BranchIdRef'
                selectedValues={selectedBranches.map((item) => ({
                  branchId: item.branchId,
                  branchName: item.branchName,
                }))}
                inputPlaceholder={'Select Branch'}
                data={branches}
                multiple
                chipsDisabled={() =>
                  returnPropsByPermissions(
                    ViewBranchCampaignsPermissions.ViewBranchCampaigns.permissionsId
                  )
                }
                isDisabled={
                  returnPropsByPermissions(
                    ViewBranchCampaignsPermissions.ViewBranchCampaigns.permissionsId
                  )
                }
                filterSelectedOptions
                getOptionSelected={(option) =>
                  selectedBranches.findIndex(
                    (item) => item.branchId === option.branchId
                  ) !== -1 || ''
                }
                displayLabel={(option) =>
                  `${option?.branchName || ''} (${option?.branchId || ''})`
                }
                chipsLabel={(option) => `${option?.branchName || ''}`}
                withoutSearchButton
                parentTranslationPath={PerformanceInsightsTranslationPath}
                translationPath={''}
                isLoading={branchesLoading}
                onChange={(event, newValue) => {
                  setSelectedBranches(newValue);
                  const branchIds = newValue.length > 0 ? newValue.map((item) => item.branchId) : null;
                  setParams((prev) => ({ ...prev, branchIds }));
                }}
                filterOptions={(options) =>
                  options.filter(
                    (option) => !params.branchIds?.includes(option.branchId)
                  )
                }
              />
            </div>
          </div>
          <div className='w-33'>
            <div className='dialog-content-item'>
              <AutocompleteComponent
                idRef='PropertiesIdRef'
                selectedValues={selectedValues.map((item) => ({
                  propertyCampaignId: item.propertyCampaignId,
                  name: item.name || '',
                }))}
                inputPlaceholder={'Select Campaign ID'}
                data={allCampaigns?.result}
                multiple
                filterSelectedOptions
                getOptionSelected={(option) =>
                  selectedValues.findIndex(
                    (item) =>
                      item.propertyCampaignId === option.propertyCampaignId
                  ) !== -1 || ''
                }
                displayLabel={(option) =>
                  `${option?.name || ''} (${option?.propertyCampaignId || ''})`
                }
                chipsLabel={(option) => `${option?.name || ''}`}
                withoutSearchButton
                parentTranslationPath={PerformanceInsightsTranslationPath}
                translationPath={''}
                isLoading={isLoading}
                onChange={(event, newValue) => {
                  setSelectedValues(newValue);
                  const uniqueCampaignIds =
                    newValue.length > 0
                      ? newValue.map((item) => item.propertyCampaignId)
                      : [];
                  setParams((prev) => ({
                    ...prev,
                    campaignIds: uniqueCampaignIds,
                  }));
                }}
                onInputKeyUp={(event) => {
                  const { value } = event.target;
                  if (searchTimer.current) clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => {
                    getAllCampaigns(value);
                  }, 1700);
                }}
                filterOptions={(options) =>
                  options.filter(
                    (option) =>
                      !params.campaignIds?.includes(option.propertyCampaignId)
                  )
                }
              />
            </div>
          </div>
          <div className='w-33'>
            <AutocompleteComponent
              idRef='MediaTypeIdRef'
              selectedValues={selectedMediaTypes.map((item) => ({
                id: item.id,
                name: item.name,
              }))}
              inputPlaceholder={'Choose a Media Type/Channel filter'}
              data={mediaTypes}
              multiple
              filterSelectedOptions
              getOptionSelected={(option) =>
                selectedMediaTypes.findIndex(
                  (item) => item.id === option.id
                ) !== -1 || ''
              }
              displayLabel={(option) => `${option?.name || ''}`}
              chipsLabel={(option) => `${option?.name || ''}`}
              withoutSearchButton
              parentTranslationPath={PerformanceInsightsTranslationPath}
              translationPath={''}
              isLoading={mediaLoading}
              onChange={(event, newValue) => {
                setSelectedMediaTypes(newValue);
                const ids =
                  newValue.length > 0 ? newValue.map((item) => item.id) : [];
                setParams((prev) => ({ ...prev, mediaDetailIds: ids }));
              }}
              onInputKeyUp={(event) => {
                const { value } = event.target;
                if (mediaSearchTimer.current)
                  clearTimeout(mediaSearchTimer.current);
                mediaSearchTimer.current = setTimeout(() => {
                  getAllMediaTypes(value);
                }, 700);
              }}
              filterOptions={(options) =>
                options.filter(
                  (option) => !params.mediaDetailIds?.includes(option.id)
                )
              }
            />
          </div>
        </div>
        <div className='d-flex-v-center-h-end gap-4'>
          <ButtonBase
            onClick={() => {
              const hasPermission = returnPropsByPermissions(
                ViewBranchCampaignsPermissions.ViewBranchCampaigns.permissionsId
              );
              const session = localStorage.getItem('session');
              const branchId = session ? JSON.parse(session).branchId : [0];

              setParams({
                pageIndex: 1,
                pageSize: 10,
                isActive: null,
                campaignIds: [],
                branchIds: hasPermission && branchId ? [branchId] :
                  !hasPermission ? [] :
                    [0],
                mediaDetailIds: [],
              });

              setSelectedMediaTypes([]);
              setSelectedValues([]);
              setAllCampaigns({ result: [], totalCount: 0 });

              if (!hasPermission || !branchId) {
                setSelectedBranches([]);
              } else {
                const matchedBranch = branches.find((b) => b.branchId === branchId);
                if (matchedBranch) {
                  setSelectedBranches([
                    {
                      branchId: branchId,
                      branchName: matchedBranch.branchName || '',
                    },
                  ]);
                } else {
                  setSelectedBranches([]);
                }
              }

              refresh();
            }}
            id='onClearedAllFiltersref'
            className='btns theme-solid bg-danger clear-all-btn max-height'
          >
            <span className='mdi mdi-filter-remove m-1' />
            {t(`clear-filters`)}
          </ButtonBase>
        </div>
      </div>
      {!loading && data.result?.length === 0 && params.branchIds?.length > 0 ? (
        <div className='text-center py-4'>
          No campaigns found for the selected branch.
        </div>
      ) : null}
      <CollapsibleTable rows={data.result} loading={loading} />
    </div>
  );
};

export default CampaignPerformance;